import { Component, OnInit, Inject } from '@angular/core';
import { MdDialogRef, MD_DIALOG_DATA, MdDialog, MdDialogConfig } from '@angular/material';
import { Agreement } from 'app/entities/transaction-profile/agreement.model';
import { EntityIdDetails } from 'app/shared/models/entity-id-details.model';
import { EntityTransactionProfileService } from 'app/entities/transaction-profile/entity-transaction-profile.service';
import { TxRuleData, TransactionRule } from 'app/entities/transaction-profile/right.model';
import { SaveAgreementComponent } from 'app/entities/transaction-profile/agreements/saveagreement/save-agreement.component';
import { Right } from 'app/entities/transaction-profile/right.model';
import { Obligation } from 'app/entities/transaction-profile/obligation.model';
import { MessageService } from 'app/services/messages.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { BankAccount } from 'app/shared/models/bank-account.model';

@Component({
    selector: 'move-transaction-rule-dialog',
    templateUrl: './move-transaction-rule-dialog.component.html',
    styleUrls: ['./move-transaction-rule-dialog.component.css']
})
export class MoveTransactionRuleDialogComponent implements OnInit {
    txRuleData: TxRuleData;
    entityIdDetails: EntityIdDetails;
    agreements: Agreement[] = [];
    filteredAgreements: Agreement[] = [];
    selectedAgreement: Agreement = null;
    agreementSearchKeyword: string = '';
    isAgreementInputFocused: boolean = false;
    isLoading = false;
    bankAccounts: BankAccount[] = [];
    selectedBankAccountId: string | null = null;

    constructor(
        public dialogRef: MdDialogRef<MoveTransactionRuleDialogComponent>,
        @Inject(MD_DIALOG_DATA) public data: { txRuleData: TxRuleData, entityIdDetails: EntityIdDetails },
        private transactionProfileService: EntityTransactionProfileService,
        private snackBarService: SnackBarService,
        private dialog: MdDialog
    ) {
        if (data) {
            this.txRuleData = data.txRuleData;
            this.entityIdDetails = data.entityIdDetails;
        }
    }

    ngOnInit() {
        this.loadAgreements();
        this.loadBankAccounts();
    }

    loadBankAccounts() {
        if (this.entityIdDetails && this.entityIdDetails.entityId) {
            this.transactionProfileService.getBankAccountsForEntity(this.entityIdDetails.entityId)
                .subscribe(
                    accounts => {
                        this.bankAccounts = accounts || [];
                        // Only set the current account if one is already assigned
                        // Do NOT auto-select the first account to avoid unintended changes
                        if (this.txRuleData && this.txRuleData.transactionRule && this.txRuleData.transactionRule.accountNo) {
                            this.selectedBankAccountId = this.txRuleData.transactionRule.accountNo;
                        } else {
                            // If no account is set, initialize to null so user must explicitly select one
                            this.selectedBankAccountId = null;
                        }
                        // User must explicitly select an account if they want to change it
                    },
                    error => {
                        console.error('Error loading bank accounts:', error);
                        this.bankAccounts = [];
                    }
                );
        }
    }

    loadAgreements() {
        if (this.entityIdDetails && this.entityIdDetails.entityId) {
            this.transactionProfileService.getAgreements(this.entityIdDetails.entityId)
                .subscribe(
                    agreements => {
                        this.agreements = agreements || [];
                        this.filteredAgreements = this.agreements;
                        // Find and select the current agreement
                        if (this.txRuleData && this.txRuleData.agreementId) {
                            const currentAgreement = this.agreements.find(a => a.id === this.txRuleData.agreementId);
                            if (currentAgreement) {
                                this.selectedAgreement = currentAgreement;
                                this.agreementSearchKeyword = currentAgreement.description;
                            }
                        }
                    },
                    error => {
                        console.error('Error loading agreements:', error);
                        this.agreements = [];
                        this.filteredAgreements = [];
                    }
                );
        }
    }

    filterAgreements() {
        if (!this.agreementSearchKeyword || this.agreementSearchKeyword.trim() === '') {
            this.filteredAgreements = this.agreements;
        } else {
            const keyword = this.agreementSearchKeyword.toLowerCase().trim();
            this.filteredAgreements = this.agreements.filter(agreement => {
                const description = agreement.description ? agreement.description.toLowerCase() : '';
                return description.includes(keyword);
            });
        }
    }

    onAgreementSearchChange() {
        this.filterAgreements();
    }

    onAgreementSearchFocus() {
        this.isAgreementInputFocused = true;
        if (this.selectedAgreement) {
            this.agreementSearchKeyword = '';
        }
        if (!this.agreementSearchKeyword || this.agreementSearchKeyword.trim() === '') {
            this.filteredAgreements = this.agreements;
        } else {
            this.filterAgreements();
        }
    }

    onAgreementSearchBlur() {
        this.isAgreementInputFocused = false;
        if (this.selectedAgreement && !this.agreementSearchKeyword) {
            this.agreementSearchKeyword = this.selectedAgreement.description;
            this.filteredAgreements = [];
        }
    }

    selectAgreement(agreement: Agreement) {
        this.selectedAgreement = agreement;
        this.agreementSearchKeyword = agreement.description;
        this.filteredAgreements = [];
    }

    openCreateAgreementDialog() {
        const config = new MdDialogConfig();
        config.disableClose = true;
        config.width = '450px';

        const createDialogRef = this.dialog.open(SaveAgreementComponent, config);
        createDialogRef.componentInstance.entityId = this.entityIdDetails.entityId;
        createDialogRef.componentInstance.action = 'Add';
        
        createDialogRef.afterClosed().subscribe(result => {
            if (result) {
                // Reload agreements and select the newly created one
                this.transactionProfileService.getAgreements(this.entityIdDetails.entityId)
                    .subscribe(agreements => {
                        this.agreements = agreements || [];
                        this.filteredAgreements = this.agreements;
                        const newAgreement = this.agreements.find(a => a.id === result);
                        if (newAgreement) {
                            this.selectAgreement(newAgreement);
                        }
                    });
            }
        });
    }

    moveTransactionRule() {
        if (!this.selectedAgreement) {
            this.snackBarService.showStatus(
                { flagResult: false, message: 'Please select a Transactional Relationship' },
                '',
                'Please select a Transactional Relationship'
            );
            return;
        }

        if (this.selectedAgreement.id === this.txRuleData.agreementId) {
            this.snackBarService.showStatus(
                { flagResult: false, message: 'Transaction rule is already in this Transactional Relationship' },
                '',
                'Transaction rule is already in this Transactional Relationship'
            );
            return;
        }

        this.isLoading = true;

        // Determine if it's a Right or Obligation based on cash flow
        const isIncoming = this.txRuleData.transactionRule && 
                          this.txRuleData.transactionRule.cashFlowName === 'Incoming';

        if (isIncoming) {
            // It's a Right
            const right = new Right();
            right.id = this.txRuleData.id;
            right.right = this.txRuleData.title;
            right.counterPartyId = this.normalizeCounterPartyId(this.txRuleData.counterPartyId);
            right.counterPartyName = this.txRuleData.counterPartyName || null;
            right.date = this.txRuleData.date;
            right.agreementId = this.selectedAgreement.id;
            // Clone the transaction rule to avoid mutating the original object
            right.transactionRule = this.cloneTransactionRule(this.txRuleData.transactionRule);
            // Update the account number only if user explicitly selected a different account
            if (right.transactionRule) {
                const originalAccountNo = (this.txRuleData.transactionRule && this.txRuleData.transactionRule.accountNo) ? this.txRuleData.transactionRule.accountNo : null;
                // Only update if the user explicitly selected a different account
                // If selectedBankAccountId is null, keep the original account (whether null or has value)
                // If selectedBankAccountId is not null and different from original, update it
                if (this.selectedBankAccountId !== null && this.selectedBankAccountId !== undefined) {
                    if (this.selectedBankAccountId !== originalAccountNo) {
                        right.transactionRule.accountNo = this.selectedBankAccountId;
                    }
                }
                // If selectedBankAccountId is null, the cloned rule already has the original accountNo, so no change needed
            }

            this.transactionProfileService.saveRight(right).subscribe(
                response => {
                    this.isLoading = false;
                    if (response.flagResult) {
                        this.dialogRef.close(true);
                        this.snackBarService.showStatus(
                            response,
                            'Transaction rule moved successfully',
                            MessageService.RIGHT_SAVE_FAILURE
                        );
                    } else {
                        this.snackBarService.showStatus(
                            response,
                            '',
                            'Failed to move transaction rule'
                        );
                    }
                },
                error => {
                    this.isLoading = false;
                    console.error('Error moving transaction rule:', error);
                    this.snackBarService.showStatus(
                        error,
                        '',
                        'Failed to move transaction rule'
                    );
                }
            );
        } else {
            // It's an Obligation
            const obligation = new Obligation();
            obligation.id = this.txRuleData.id;
            obligation.obligation = this.txRuleData.title;
            obligation.counterPartyId = this.normalizeCounterPartyId(this.txRuleData.counterPartyId);
            obligation.counterPartyName = this.txRuleData.counterPartyName || null;
            obligation.date = this.txRuleData.date;
            obligation.agreementId = this.selectedAgreement.id;
            // Clone the transaction rule to avoid mutating the original object
            obligation.transactionRule = this.cloneTransactionRule(this.txRuleData.transactionRule);
            // Update the account number only if user explicitly selected a different account
            if (obligation.transactionRule) {
                const originalAccountNo = (this.txRuleData.transactionRule && this.txRuleData.transactionRule.accountNo) ? this.txRuleData.transactionRule.accountNo : null;
                // Only update if the user explicitly selected a different account
                // If selectedBankAccountId is null, keep the original account (whether null or has value)
                // If selectedBankAccountId is not null and different from original, update it
                if (this.selectedBankAccountId !== null && this.selectedBankAccountId !== undefined) {
                    if (this.selectedBankAccountId !== originalAccountNo) {
                        obligation.transactionRule.accountNo = this.selectedBankAccountId;
                    }
                }
                // If selectedBankAccountId is null, the cloned rule already has the original accountNo, so no change needed
            }

            this.transactionProfileService.saveObligation(obligation).subscribe(
                response => {
                    this.isLoading = false;
                    if (response.flagResult) {
                        this.dialogRef.close(true);
                        this.snackBarService.showStatus(
                            response,
                            'Transaction rule moved successfully',
                            MessageService.OBLIGATION_SAVE_FAILURE
                        );
                    } else {
                        this.snackBarService.showStatus(
                            response,
                            '',
                            'Failed to move transaction rule'
                        );
                    }
                },
                error => {
                    this.isLoading = false;
                    console.error('Error moving transaction rule:', error);
                    this.snackBarService.showStatus(
                        error,
                        '',
                        'Failed to move transaction rule'
                    );
                }
            );
        }
    }

    getAccountDisplayName(accountNo: string): string {
        if (!accountNo) {
            return '-';
        }
        const account = this.bankAccounts.find(acc => acc.accountNumber === accountNo);
        if (account) {
            return account.accountName ? `${account.accountNumber} - ${account.accountName}` : account.accountNumber;
        }
        return accountNo;
    }

    /**
     * Creates a deep clone of the TransactionRule object to avoid mutating the original.
     * This prevents unintended side effects on the parent component's data.
     */
    private cloneTransactionRule(original: TransactionRule): TransactionRule {
        if (!original) {
            return null;
        }
        const cloned = new TransactionRule();
        cloned.id = original.id;
        cloned.accountNo = original.accountNo;
        cloned.name = original.name;
        cloned.txnDescription = original.txnDescription;
        cloned.cashFlow = original.cashFlow;
        cloned.amountFrom = original.amountFrom;
        cloned.amountTo = original.amountTo;
        cloned.maxAmount = original.maxAmount;
        cloned.maxAmountPeriod = original.maxAmountPeriod;
        cloned.maxAmountPeriodName = original.maxAmountPeriodName;
        cloned.maxAmountPeriodFrom = original.maxAmountPeriodFrom;
        cloned.maxAmountPeriodTo = original.maxAmountPeriodTo;
        cloned.frequency = original.frequency;
        cloned.frequencyPeriod = original.frequencyPeriod;
        cloned.frequencyPeriodName = original.frequencyPeriodName;
        cloned.frequencyPeriodFrom = original.frequencyPeriodFrom;
        cloned.frequencyPeriodTo = original.frequencyPeriodTo;
        cloned.counterPartyId = original.counterPartyId;
        cloned.counterPartyName = original.counterPartyName;
        cloned.counterPartyAccountNumber = original.counterPartyAccountNumber;
        cloned.counterPartyAccounts = original.counterPartyAccounts ? [...original.counterPartyAccounts] : null;
        cloned.originCountry = original.originCountry;
        cloned.cashFlowName = original.cashFlowName;
        cloned.originCountryName = original.originCountryName;
        cloned.reviewStatus = original.reviewStatus;
        cloned.agreementId = original.agreementId;
        cloned.lastModifiedOn = original.lastModifiedOn;
        cloned.createdOn = original.createdOn;
        return cloned;
    }

    close() {
        this.dialogRef.close(false);
    }

    /**
     * Normalizes counterPartyId: converts empty strings, null, undefined, or 0 to null.
     * This ensures the backend receives null instead of empty string, preventing NullPointerException.
     */
    private normalizeCounterPartyId(counterPartyId: any): number | null {
        if (counterPartyId === null || counterPartyId === undefined || counterPartyId === '' || counterPartyId === 0) {
            return null;
        }
        // Convert string to number if it's a valid number string
        if (typeof counterPartyId === 'string' && counterPartyId.trim() !== '') {
            const num = Number(counterPartyId);
            return isNaN(num) || num === 0 ? null : num;
        }
        // Return as number if it's already a valid number
        const num = Number(counterPartyId);
        return isNaN(num) || num === 0 ? null : num;
    }
}




// WEBPACK FOOTER //
// ./src/app/entities/transaction-profile/transaction-rule/move-transaction-rule-dialog.component.ts